import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { messagesChannelManager } from '../channels/messages_channel'
import useAuth from '../utilities/hooks/useAuth'

export const UnreadMessagesContext = createContext(null)

export function UnreadMessagesProvider({ children }) {
  const [unreadCount, setUnreadCount] = useState(0)
  const { currentUser, authHeaders } = useAuth()
  const subscribedRef = useRef(false)

  useEffect(() => {
    if (currentUser?.unreadMessagesCount !== undefined) {
      const direct = currentUser.unreadMessagesCount || 0
      const group = currentUser.unreadGroupMessagesCount || 0
      setUnreadCount(direct + group)
    }
  }, [currentUser])

  useEffect(() => {
    if (!authHeaders || !currentUser || subscribedRef.current) {
      return
    }

    subscribedRef.current = true
    messagesChannelManager.subscribe(authHeaders)

    const handleMessage = (data) => {
      if (data.type === 'new_message' || data.type === 'new_group_message') {
        const senderId = data.message?.attributes?.sender?.id || data.message?.sender?.id
        if (senderId && senderId.toString() !== currentUser.id.toString()) {
          setUnreadCount((prev) => prev + 1)
        }
      } else if (data.type === 'message_read') {
        setUnreadCount((prev) => Math.max(0, prev - 1))
      }
    }

    messagesChannelManager.addCallback(handleMessage)

    return () => {
      messagesChannelManager.removeCallback(handleMessage)
      subscribedRef.current = false
    }
  }, [authHeaders, currentUser])

  const incrementCount = useCallback(() => {
    setUnreadCount((prev) => prev + 1)
  }, [])

  const decrementCount = useCallback(() => {
    setUnreadCount((prev) => Math.max(0, prev - 1))
  }, [])

  const decrementBy = useCallback((amount) => {
    setUnreadCount((prev) => Math.max(0, prev - amount))
  }, [])

  const resetCount = useCallback(() => {
    setUnreadCount(0)
  }, [])

  const value = useMemo(
    () => ({
      unreadCount,
      incrementCount,
      decrementCount,
      decrementBy,
      resetCount
    }),
    [unreadCount, incrementCount, decrementCount, decrementBy, resetCount]
  )

  return <UnreadMessagesContext.Provider value={value}>{children}</UnreadMessagesContext.Provider>
}

UnreadMessagesProvider.propTypes = {
  children: PropTypes.node.isRequired
}
